import { useEffect, useState, type JSX } from 'react';
import { Avatar, type AvatarSize } from '@viniciomendesr/rayrovisk-design-system';
import { useProfileAvatar } from '../api/queries';

/**
 * The profile picture she chose, once it has been stored in our own bucket.
 *
 * The bytes are fetched rather than pointed at: the avatar route is authorized
 * and `<img src>` cannot carry a bearer token. The object URL is revoked when
 * the key changes or the component goes away.
 *
 * No key, or a fetch that fails, falls through to the design system's
 * initials. That is a normal state here rather than an error.
 */
export function ProfileAvatar({
  objectKey,
  name,
  size = 'md'
}: {
  objectKey: string | undefined;
  name: string;
  size?: AvatarSize;
}): JSX.Element {
  const [src, setSrc] = useState<string | undefined>(undefined);
  const avatar = useProfileAvatar(objectKey);

  useEffect(() => {
    if (!avatar.data) {
      setSrc(undefined);
      return;
    }

    const url = URL.createObjectURL(avatar.data);
    setSrc(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [avatar.data]);

  return <Avatar src={src} name={name} size={size} />;
}
